/* eslint-disable eqeqeq */
/**
 * Phase 6 Vendor-Port Wave 1 — UV-C SELECTOR (§33.6).
 *
 * React port of components/uvc/view/uvc.selector.js (1,935) + its 6 templates,
 * into the 6e2c-0+1 component-selector chassis (§30) as the 'uvc' tab body.
 * BOARD only (header li.hide). Opened via the ACCESSORIES-TAB path: the uvc
 * accessory form's "Select" heading (AccessorySelectHeading type="uvc") emits
 * rating_mode:activate {name:'uvc', current_model:{id:accessoryId}, section} —
 * the React analogue of the legacy tab.accessories openRatingMode (.rate click,
 * data-name=='uvc' → #rating_mode_uvc_content). The §30.4 fan-out key is
 * therefore uvc_<accessoryId>:selector:<action>.
 *
 * UVC RATING IS SERVER-SIDE (no external vendor, no /rating/ dispatch): RATE is
 * a PATCH ahu/uvcs/<id> with the ~25 master_id/series/qty/target fields plus
 * rate:true; the backend computes in-place and echoes back (rows, the per-kind
 * qtys_model lists, notes, lamps_per_row). SELECT is a models_field PATCH +
 * per-row actual-qty PATCHes + a params_field PATCH.
 *
 * ★ DATA-SAFETY (§4 + the 6e2a userArmed gate — HARD REQUIREMENT):
 *   - NOTHING fires on mount / open / tab-switch. The LEGACY view fires a
 *     `self.model.save({target_surface_id, rate:true})` ON INIT (via
 *     setTargetSurfaceDefaultID, when the model has no `rows`) — that is exactly
 *     the legacy "save on read" the userArmed gate must SUPPRESS. We DO NOT
 *     replicate it: a fresh UVC renders its initial/empty rated state with zero
 *     writes. The default-target render is computed client-side from the GET.
 *   - The RATE PATCH + SELECT PATCHes + actual-qty PATCHes are armed ONLY by a
 *     real user #rate / #select click (userArmed ref). On dev3 every UVC is
 *     rate=false / master_lamp_id 0, so the rated-output render path is
 *     UNREACHABLE without firing a fresh rate — which smoke MUST NOT do. Smoke
 *     is open / render / cancel only; the rate+select write paths are verified
 *     by code review and parked to throwaway-AHU QA.
 *
 * WAVE-1 SCOPE NOTES (documented in the build report DEVIATIONS):
 *   - The dependency-driven option NARROWING (legacy parameterPossibleIDs ×
 *     mergeIdsWithCMS, a per-parameter CMS-params graph) is NOT reproduced;
 *     each select is seeded from its FULL catalog list (a superset — the saved
 *     value always renders). The narrowing only matters once a rate fires
 *     (unreachable read-only on dev3). Deferred.
 *   - The draw popup (copy of coil's, 806-line tmpl) + the print tmpl (1,068)
 *     are STUBBED (button present, opens a placeholder) — they only have content
 *     after a rate, also unreachable on dev3. Deferred to write-QA.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ahuBoardBus } from '../../bus/ahuBoardBus';
import { optionLabel, type OptionRow } from '../formKit';
import {
  ahuComponentFormsApi,
  useGetComponentDetailQuery,
  useGetComponentCmsDocQuery,
  useGetMasterListByUrlQuery,
} from 'app/services/ahuComponentFormsApi';
import { useAppDispatch } from 'app/hooks';
import {
  UVC_CMS_DOC_URL,
  cmsOptionList,
  cmsSelectedLabel,
  type UvcCmsDoc,
} from './uvcCms';
import {
  useRateUvcMutation,
  usePatchUvcModelsFieldMutation,
  usePatchUvcParamsFieldMutation,
  usePatchUvcActualQtyMutation,
} from './uvcSelectorApi';
import {
  INTLK_CONTACT_CONTROLLER_SERIES_ID,
  UVC_CATALOG_URLS,
  UVC_DEPENDENCY_GRAPH,
  UVC_INTER_LOCK_SWITCH_PARAMS,
  UVC_MFG_ID,
  UVC_PARAMS_BASE,
  UVC_PARAMS_FIELDS,
  UVC_PART_ID,
  UVC_REQUIRED_FIELDS,
  defaultTargetIdForSection,
  type UvcCatalogKey,
} from './uvcSelectorData';
import { useDependencyGraph, type LoadParamsFn } from './useDependencyGraph';
import { pendingSelectWrites, selectWriteKey, trackSelectWrites } from './selectWriteGate';
import { useGetAhuBoardProjectQuery } from 'app/services/ahuBoardApi';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  UvcRatedOutputs,
  UvcOutputFooter,
  excludeSelectedModel,
  narrowCatalog,
  type AllPartRow,
  type OutputTab,
  type UvcAllParts,
} from './uvcRatedOutputs';
import type { ComponentFormShim } from '../componentFormTypes';
import type { SelectorTabProps } from './selectorTypes';
import type { IUvcRow } from './uvcSelectorApi';
import { sweepModelFieldInputs, mergeSweptFields } from './modelFieldSweep';
import { track } from 'app/analytics';

type TabName = 'Project' | 'Properties' | 'Lamp' | 'Options';

/**
 * Read every catalog list (RTKQ dedups + caches). Each hook is called DIRECTLY
 * (no nested closure) so the hook-call order is stable and rules-of-hooks holds
 * — a closure-wrapped call here previously left the catalog subscriptions
 * un-fired (the queries hung `pending`, the selector stuck on the preloader).
 */
function useUvcCatalogs() {
  return {
    warning_sign: useGetMasterListByUrlQuery(UVC_CATALOG_URLS.warning_sign),
    intlk_contact_controller: useGetMasterListByUrlQuery(UVC_CATALOG_URLS.intlk_contact_controller),
    inter_lock_switch: useGetMasterListByUrlQuery(UVC_CATALOG_URLS.inter_lock_switch),
    toggle_switch: useGetMasterListByUrlQuery(UVC_CATALOG_URLS.toggle_switch),
    hour_meter: useGetMasterListByUrlQuery(UVC_CATALOG_URLS.hour_meter),
    radio_meter: useGetMasterListByUrlQuery(UVC_CATALOG_URLS.radio_meter),
    current_sensor: useGetMasterListByUrlQuery(UVC_CATALOG_URLS.current_sensor),
    lamp_holder: useGetMasterListByUrlQuery(UVC_CATALOG_URLS.lamp_holder),
    balast_connector_kit: useGetMasterListByUrlQuery(UVC_CATALOG_URLS.balast_connector_kit),
    power_supply: useGetMasterListByUrlQuery(UVC_CATALOG_URLS.power_supply),
    frameing_kit: useGetMasterListByUrlQuery(UVC_CATALOG_URLS.frameing_kit),
    lamp: useGetMasterListByUrlQuery(UVC_CATALOG_URLS.lamp),
    uv_repeat_kit: useGetMasterListByUrlQuery(UVC_CATALOG_URLS.uv_repeat_kit),
    lamp_finish: useGetMasterListByUrlQuery(UVC_CATALOG_URLS.lamp_finish),
    lamp_output: useGetMasterListByUrlQuery(UVC_CATALOG_URLS.lamp_output),
    target: useGetMasterListByUrlQuery(UVC_CATALOG_URLS.target),
    target_surface: useGetMasterListByUrlQuery(UVC_CATALOG_URLS.target_surface),
    pricing_methods: useGetMasterListByUrlQuery(UVC_CATALOG_URLS.pricing_methods),
    powered_by: useGetMasterListByUrlQuery(UVC_CATALOG_URLS.powered_by),
  };
}

/** A bare catalogue GET for the dependency cascade (legacy fn.uni_tools.get).
 * These are per-(field, parent-values) URLs, not RTKQ-cached entities. */
async function getJson(url: string): Promise<unknown[]> {
  try {
    const res = await fetch(url, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!res.ok) return [];
    const body = await res.json();
    return Array.isArray(body) ? body : ((body as { results?: unknown[] })?.results ?? []);
  } catch {
    return [];
  }
}

const num = (v: string): number | null => {
  if (v === '' || v === 'None') return null;
  const n = parseInt(v, 10);
  return Number.isNaN(n) ? null : n;
};

export const UvcSelector = ({
  modelId,
  ahu,
  activate,
  setSelectEnabled,
}: SelectorTabProps) => {
  // ---- read-only loads (fire on mount; never mutate) ----
  // refetchOnMountOrArgChange: the selector wants a FRESH read of the component
  // (legacy current_model.fetch() on open). It also self-heals if the cache
  // entry was left mid-flight/aborted by a competing form subscription that
  // unmounted (otherwise a `pending` shared entry never re-resolves and the
  // selector would hang on the preloader).
  const {
    data: detail,
    isLoading: detailLoading,
    isError: detailError,
    fulfilledTimeStamp: detailFulfilledAt,
  } = useGetComponentDetailQuery(
    { plural: 'uvcs', id: modelId },
    { refetchOnMountOrArgChange: true }
  );

  // ---- ★ seed only from a row that POSTDATES this open (the reopen gate) ----
  // "Rate → Select → reopen shows an EMPTY Output section" (dev shows the
  // saved sheet). The rated sheet is seeded below off this detail GET, and
  // `getComponentDetail` carries no tags: RTK Query keeps the unsubscribed
  // entry for its default 60 s while the chassis mounts a FRESH selector on
  // every open (`key={dialog.openSeq}`, ComponentSelectorDialog.tsx). So a
  // reopen inside that minute is served the CACHED — pre-Select — row
  // synchronously. `refetchOnMountOrArgChange` does re-pull, but `isLoading`
  // is false the whole time (true only on the very first ever load) and the
  // seed latches `seeded.current = modelId`, which blocks the re-seed when the
  // fresh row lands. The pre-Select row has `rows: null` (RATE never writes —
  // `AHUAccessoryUVC.save()` returns early on `self.rate`, so the whole rated
  // sheet only exists on the server after SELECT persists product_series /
  // lamp_output / target_height), so `setRated` never fires and `.output-part`
  // renders empty until a page reload.
  //
  // Legacy cannot have this: `render()` re-reads the row itself —
  // `self.current_model.fetch().then(() => self.render_outputs(...))`
  // (uvc.selector.js:1838-1842) — so its reopen always draws the saved sheet.
  //
  // Same gate, same reasoning as the coil, energy wheel, gas heater and PHX
  // selectors. ⚠ `isFetching` is not a usable gate: RTK Query starts the
  // refetch from an effect inside the hook, so the first render after mount
  // still reports `isFetching: false` next to the stale `data` — the seed
  // effect runs in that very commit. `fulfilledTimeStamp` is stamped when a
  // request FULFILS, so a row cached before this dialog opened can never
  // satisfy `>= openedAt`.
  const openedAt = useRef(Date.now());
  // …and a GET issued while the previous Select's PATCH chain is still in
  // flight would fulfil after `openedAt` while STILL returning the pre-Select
  // row. When this open follows a Select whose chain has not settled, hold the
  // gate open (`Infinity`) until it does, then re-read and gate on THAT moment.
  const pendingWrites = useRef(pendingSelectWrites(selectWriteKey('uvcs', modelId)));
  const [gateAt, setGateAt] = useState<number>(() =>
    pendingWrites.current ? Number.POSITIVE_INFINITY : openedAt.current
  );
  const dispatch = useAppDispatch();
  useEffect(() => {
    const writes = pendingWrites.current;
    if (!writes) return;
    let alive = true;
    void writes.then(() => {
      if (!alive) return;
      // stamped BEFORE the refetch is dispatched, so only its (later)
      // fulfilment can satisfy the gate.
      const at = Date.now();
      void dispatch(
        ahuComponentFormsApi.endpoints.getComponentDetail.initiate(
          { plural: 'uvcs', id: modelId },
          { forceRefetch: true, subscribe: false }
        )
      );
      setGateAt(at);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId]);
  // `isError` is the escape hatch: a failed read seeds from whatever is cached
  // rather than pinning the preloader on for ever.
  const detailFresh = detailError || (detailFulfilledAt != null && detailFulfilledAt >= gateAt);
  // W1.5 (W1-2/W1-3): the UVC CMS doc — the SOLE source of the dependent-select
  // display labels (mfg/series/power_supply.series/voltage/lamp.series). Legacy
  // fn['current.cms.uvc']; resolved through uvcCms.ts. URL-keyed 24h cache.
  const { data: cmsDoc, isLoading: cmsLoading } =
    useGetComponentCmsDocQuery(UVC_CMS_DOC_URL);
  const cats = useUvcCatalogs();
  const catalogsLoading = Object.values(cats).some((q) => q.isLoading);

  // ---- mutations (ALL userArmed-gated; never auto-fired — data-safety) ----
  const [rateUvc] = useRateUvcMutation();
  const [patchModels] = usePatchUvcModelsFieldMutation();
  const [patchParams] = usePatchUvcParamsFieldMutation();
  const [patchActualQty] = usePatchUvcActualQtyMutation();

  // the selector's own root — the scope legacy's `.model_field` sweep runs
  // over (`self.$el`, uvc.selector.js:1651). All four input panes stay mounted.
  const rootRef = useRef<HTMLDivElement | null>(null);

  // ---- state model (legacy self.state) + init snapshot ----
  const state = useRef<IUvcRow>({});
  const init = useRef<IUvcRow>({});
  const seeded = useRef<number | null>(null);
  const [, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((v) => v + 1), []);

  // userArmed gate: arms ONLY on a real #rate/#select click. Nothing on
  // mount/open/tab-switch arms it. The legacy save-on-init is intentionally NOT
  // replicated (see file header data-safety).
  const userArmed = useRef(false);

  // the rated model echoed by RATE (drives <UvcRatedOutputs>). Seeded from the
  // GET when the saved row already carries `rows` and is not `is_reset` — i.e.
  // an already-SELECTed UVC reopened (see the seed effect); otherwise null
  // (initial/empty state).
  const [rated, setRated] = useState<IUvcRow | null>(null);
  const ratedDatetime = useRef<string | null>(null);
  // W1-5 parts toggle. Owned here because legacy's `showParts` puts the class
  // on `.uvc-output` — which IS `.output-part`, rendered below (js:1884-1896).
  const [partsMode, setPartsMode] = useState<'scheduled' | 'all'>('scheduled');
  // active OUTPUT tab, lifted for the same reason: legacy's activateOutputLabel
  // (js:887-894) shows `.footer-left` only for the tab whose <a> carries the
  // `components` class and hides it for Electrical / Notes — and `.footer-left`
  // is a sibling of `.output-part`, rendered here.
  const [outputTab, setOutputTab] = useState<OutputTab>('Components');
  // `.footer-left` visibility, mirroring legacy's inline display writes rather
  // than deriving it from `rated` + the active output tab. `#rating_mode_uvc_
  // content .footer-left` is display:none in CSS (common.css:17542) and legacy
  // only ever un-hides it from two places — the rate `.always` (js:1298) and an
  // output-tab CLICK (activateOutputLabel, js:887-894). The reopen path that
  // restores a saved rating (js:1838-1847) calls render_outputs with its
  // `.footer-left` un-hide line COMMENTED OUT, so reopening an already-selected
  // UVC shows the parts table with no footer until the user clicks an output
  // tab. Deriving from `rated` made it visible on reopen.
  const [footerVisible, setFooterVisible] = useState(false);
  // legacy `click .output_tab` → activateOutputLabel: block for the tab whose
  // <a> carries `components`, none for Electrical / Notes. Fires on every click,
  // including a re-click of the already-active tab — which is how the footer
  // becomes reachable after a reopen.
  const onOutputTab = useCallback((t: OutputTab) => {
    setOutputTab(t);
    setFooterVisible(t === 'Components');
  }, []);

  // active input tab + active output tab (legacy li.active swap).
  const [activeTab, setActiveTab] = useState<TabName>('Properties');

  // ---- Project tab (legacy tmpl:23-68 — project.name / ahu.tag /
  // "section.name : section.tag"). `activate` carries none of these: the
  // accessory opener builds its payload from the form shim, so `project` and
  // `ahu` are absent outright and `section` used to be `{id, ...aw dims}`.
  // The AHU arrives as the sibling prop the chassis passes (the EW selector
  // makes the same point at :734), the project is one cached GET off its
  // project_id, and the section row now rides along on the activation.
  const boardSection = (activate.section ?? undefined) as
    | { name?: string; tag?: string; type?: string }
    | undefined;
  const { data: boardProject } = useGetAhuBoardProjectQuery(
    (ahu as { project_id?: number } | null | undefined)?.project_id ?? skipToken
  );
  const projectName = (boardProject as { name?: string } | undefined)?.name;
  const ahuTag = (ahu as { tag?: string } | null | undefined)?.tag;
  // legacy: `{{section.name}}{{#if section.tag}} : {{section.tag}}{{/if}}`.
  const sectionLabel = boardSection?.name
    ? `${boardSection.name}${boardSection.tag ? ` : ${boardSection.tag}` : ''}`
    : '';

  const sectionType = boardSection?.type;
  const defaultTargetId = defaultTargetIdForSection(sectionType);

  // Target Surface options — legacy getTargetDefaultID (js:636-651): find the
  // uvc_target row whose target_value matches the SECTION TYPE, then keep only
  // the uvc_target_surface JOIN rows bound to it. `undefined` section type (no
  // match) falls back to the full list rather than an empty select.
  //
  // ★ Each row is a JOIN row: `row.id` is its own pk, `row.surface.id` is the
  // surface pk. The <select> is keyed on the SURFACE pk (legacy
  // uvc.selector.tmpl / `el.surface_id == e.target.value`), but the rate and
  // select PATCHes send `target_surface_id` = the JOIN row's pk (legacy
  // `self.rate_target_id.id`, js:625/:684). Hoisted out of the render body so
  // both the payload builders and the <select> read the same list.
  const targetSurfaceRows = useMemo(() => {
    const surfaces = (cats.target_surface.data as OptionRow[] | undefined) ?? [];
    const targets = (cats.target.data as OptionRow[] | undefined) ?? [];
    const tv = sectionType === 'user_defined_component' ? 'uds' : sectionType;
    const targetId = tv ? targets.find((r) => r.target_value === tv)?.id : undefined;
    return targetId != null ? surfaces.filter((r) => r.target_id === targetId) : surfaces;
  }, [cats.target_surface.data, cats.target.data, sectionType]);

  /** legacy `self.rate_target_id` — the join row for the selected surface. */
  const joinRowIdForSurface = useCallback(
    (surfaceId: unknown): number | undefined =>
      targetSurfaceRows.find(
        (r) => String((r.surface as OptionRow)?.id ?? r.id) === String(surfaceId)
      )?.id as number | undefined,
    [targetSurfaceRows]
  );

  // ---- seed state + init once GET resolves ----
  useEffect(() => {
    if (!detail) return;
    // ★ the reopen gate (see `detailFresh` above). The seed LATCHES, so it must
    // not run against a row that predates this open.
    if (!detailFresh) return;
    if (seeded.current === modelId) return;
    const m = detail as IUvcRow;
    // seed the state model from the saved component (legacy: state.set(model) +
    // params_field expansion). mfg defaults to 42 (the only UVC mfg).
    const params = (m.params_field as Record<string, unknown>) ?? {};
    state.current = {
      mfg_id: m.product_mfg_id ?? UVC_MFG_ID,
      product_series_id: m.product_series_id,
      power_supply_series_id: m.power_supply_series_id,
      power_supply_voltage_id: m.voltage_id,
      powered_by_id: m.powered_by_id ?? 1,
      lamp_series_id: m.lamp_series_id,
      lamp_set_qty: m.lamp_set_qty,
      lamp_output_id: m.lamp_output_id,
      lamp_finish_id: m.lamp_finish_id,
      lamp_holder_id: m.master_lamp_holder_id,
      lamp_to_surface_distance: m.lamp_to_surface_distance,
      balast_connector_kit_id: m.master_balast_connector_kit_id,
      uv_repeat_kit_series_id: m.uv_repeat_kit_series_id,
      current_sensor_id: m.master_current_sensor_id,
      radio_meter_id: m.master_radio_meter_id,
      hour_meter_id: m.master_hour_meter_id,
      toggle_switch_id: m.master_toggle_switch_id,
      intlk_contact_controller_id: m.master_intlk_contact_controller_id,
      inter_lock_switch_id: m.master_inter_lock_switch_id,
      warning_sign_id: m.master_warning_sign_id,
      frameing_kit_id: m.master_frameing_kit_id,
      uvc_target_target_surface_id: m.uvc_target_target_surface_id ?? defaultTargetId,
      target_width: m.target_width,
      target_height: m.target_height,
      ...params,
    };
    init.current = { ...state.current };
    seeded.current = modelId;
    // Already-rated model → show its saved rated outputs, exactly as legacy
    // `render()` does (uvc.selector.js:1831-1846):
    //
    //   if (current_model.is_reset)  → .output-part emptied, footer hidden
    //   else if (current_model.rows) → fetch() then render_outputs()
    //
    // `rows` is a server-side @property (models_accessory_uvc.py:147) off the
    // PERSISTED product_series / target_height / lamp_output, so every
    // SELECTed UVC carries it and the whole sheet (`*_qtys_model`, the scalar
    // `*_model` rows, `lamps_per_row`, `notes`) rides along on the same GET.
    // `is_reset` (:100) is true once the section's own airway dims have drifted
    // from the dims the selection was made against — legacy treats that
    // selection as stale and draws no sheet.
    if (m.rows && !m.is_reset) {
      setRated(m);
      ratedDatetime.current = (m.rating_datetime as string) ?? null;
    }
    bump();
  }, [detail, detailFresh, modelId, defaultTargetId, bump]);

  // ---- the dependency cascade (legacy DependenciesHandler) ----------------
  // Wave-1 shipped every select seeded from its FULL catalog and NO default
  // picking, on the reasoning that narrowing "only matters once a rate fires".
  // It matters on open: legacy's applyParameterNewValues is what puts
  // "RLM Extreme" / "Xbox-PS" / "INSTL-KIT" / the whole Options tab into the
  // dialog before the user touches anything (default_values for the first two,
  // the sole-option arm for the rest). Without it the port opened blank.
  //
  // Nothing here writes. setStateValue mutates the local state model only — it
  // deliberately does NOT call markChanged()/setRated(null), so the open-time
  // cascade cannot arm the unsaved-changes guard or the RATE button. Legacy's
  // save-on-init stays un-replicated (see the file header).
  const getValue = useCallback(
    (field: string) => state.current[field] as number | string | null | undefined,
    []
  );
  const setStateValue = useCallback(
    (field: string, value: number | string | null) => {
      state.current = { ...state.current, [field]: value };
      bump();
    },
    [bump]
  );

  const loadParams = useCallback<LoadParamsFn>(async (field, parents) => {
    // legacy __checkParents (js:831-848): a null REQUIRED parent aborts the
    // load entirely; a null optional parent is just left out of the filters.
    const filters: string[] = [];
    for (const p of parents) {
      const v = state.current[p];
      if (v == null || v === '' || v === 'None') {
        if (UVC_REQUIRED_FIELDS.has(p)) return [];
        continue;
      }
      filters.push(`${p}=${encodeURIComponent(String(v))}`);
    }

    // inter_lock_switch has its own legacy URL + the 832 sentinel, and
    // mergeIdsWithCMS keeps only the FIRST row (js:728-741) — so this select
    // renders exactly one option.
    if (field === 'inter_lock_switch_id') {
      const series = state.current.intlk_contact_controller_id
        ? INTLK_CONTACT_CONTROLLER_SERIES_ID
        : null;
      const rows = await getJson(
        `${UVC_INTER_LOCK_SWITCH_PARAMS}?intlk_contact_controller_series_id=${series}`
      );
      const first = rows[0] as { id?: number } | undefined;
      return first?.id != null ? [first.id] : [];
    }

    if (!UVC_PARAMS_FIELDS.has(field)) return [];

    // product_series_id carries the extra &part_id=96 (js:860-866).
    const qs = filters.join('&');
    const url =
      `${UVC_PARAMS_BASE}${field}/${qs ? `?${qs}` : ''}` +
      (field === 'product_series_id' ? `${qs ? '&' : '?'}part_id=${UVC_PART_ID}` : '');
    return (await getJson(url)) as Array<number | string>;
  }, []);

  const graph = useDependencyGraph({
    spec: UVC_DEPENDENCY_GRAPH,
    loadParams,
    getValue,
    setValue: setStateValue,
  });
  // useDependencyGraph hands back a fresh handle every render, so the field
  // handlers reach it through a ref rather than listing it as a dep.
  const graphRef = useRef(graph);
  graphRef.current = graph;

  // mount cascade — legacy `dependenciesHandler.loadOptionsData('mfg_id')`
  // (js:620), fired once the state model is seeded. mfg_id is always 42, so the
  // whole graph reloads from the root exactly as it does on open in dev.
  const cascadeFired = useRef(false);
  useEffect(() => {
    if (cascadeFired.current) return;
    if (seeded.current !== modelId) return;
    if (state.current.mfg_id == null) return;
    cascadeFired.current = true;
    graph.onParentChange('mfg_id');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, detail]);

  // Two fields sit OUTSIDE the graph: legacy seeds their possible-id sets by
  // hand (js:612-619) and lets the same applyParameterNewValues re-pick run.
  //  - toggle_switch_id is a CUSTOM_COLLECTION seeded from its whole master
  //    list, which holds exactly one row on dev3 — so the sole-option arm picks
  //    "UVR-WP-TS", which is why dev opens with it and the port opened on "--".
  //  - uvc_target_target_surface_id's list is narrowed to the section's target;
  //    a saved id outside it falls back to the section-type default.
  const directSeeded = useRef(false);
  useEffect(() => {
    if (directSeeded.current) return;
    if (seeded.current !== modelId) return;
    if (catalogsLoading) return;
    directSeeded.current = true;
    const patch: IUvcRow = {};
    const toggles = (cats.toggle_switch.data as OptionRow[] | undefined) ?? [];
    if (state.current.toggle_switch_id == null && toggles.length === 1) {
      patch.toggle_switch_id = toggles[0].id as number;
    }
    const surfaces = (cats.target_surface.data as OptionRow[] | undefined) ?? [];
    const targets = (cats.target.data as OptionRow[] | undefined) ?? [];
    const tv = sectionType === 'user_defined_component' ? 'uds' : sectionType;
    const targetId = tv ? targets.find((r) => r.target_value === tv)?.id : undefined;
    if (targetId != null) {
      const allowed = surfaces
        .filter((r) => r.target_id === targetId)
        .map((r) => String((r.surface as OptionRow)?.id ?? r.id));
      const current = state.current.uvc_target_target_surface_id;
      if (current == null || !allowed.includes(String(current))) {
        patch.uvc_target_target_surface_id = defaultTargetId;
      }
    }
    if (Object.keys(patch).length) {
      state.current = { ...state.current, ...patch };
      bump();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId, detail, catalogsLoading]);

  // checkRequiredFields → #rate gate (legacy checkAndUpdateRequiredFields).
  // #select stays disabled until a RATE produces rows (legacy
  // checkButtonsAvailability removes #select.non-clickable only after rate).
  const requiredFilled = useMemo(() => {
    const s = state.current;
    for (const name of UVC_REQUIRED_FIELDS) {
      const v = s[name];
      if (v == null || v === '' || v === 'None') return false;
    }
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.current, detail]);

  // The chassis #select gate. Enabled once a rate has produced outputs OR the
  // model arrived already-rated (legacy: #select clickable after rate / on a
  // rated model). NOT enabled on open of an unrated UVC.
  useEffect(() => {
    setSelectEnabled(Boolean(rated));
  }, [rated, setSelectEnabled]);

  // #rate gate (legacy checkRequiredFields toggles #rate.non-clickable on the
  // chassis DOM directly). The chassis renders #rate as a static button; we
  // mirror legacy's exact DOM surgery so the button greys when required fields
  // are unfilled OR an unrated render is showing. (doRate also hard-gates on
  // requiredFilled, so this is purely the visual affordance.) After a rate the
  // legacy makes #rate non-clickable until the next field change — mirrored.
  useEffect(() => {
    const btn = document.getElementById('rate');
    if (!btn) return;
    const clickable = requiredFilled && !rated;
    btn.classList.toggle('non-clickable', !clickable);
  });

  // mark a real user change (arms the unsaved-guard + lets the chassis enable
  // the save-changes popup). Does NOT itself write.
  const markChanged = useCallback(() => {
    ahuBoardBus.emit('rating_mode:mounting:changed');
  }, []);

  // ---- field handlers ----
  const onField = useCallback(
    (name: string, raw: string) => {
      const value = name.endsWith('_id') ? num(raw) : raw;
      state.current = { ...state.current, [name]: value };
      // a search/model field change clears the prior rated outputs (legacy:
      // #rate clickable, #select non-clickable, .output-part emptied, and
      // `.footer-left` re-hidden — changeSearchField js:903 / changeModelField
      // js:929).
      setRated(null);
      setFooterVisible(false);
      markChanged();
      bump();
      // legacy changeSearchField ends with `dependenciesHandler.loadOptionsData
      // (name)` (js:918) — re-narrow every descendant and re-pick the ones the
      // new list invalidates.
      graphRef.current.onParentChange(name);
    },
    [markChanged, bump]
  );

  const onNumberField = useCallback(
    (name: string, raw: string | null) => {
      state.current = { ...state.current, [name]: raw == null ? null : raw };
      setRated(null);
      setFooterVisible(false);
      markChanged();
      bump();
    },
    [markChanged, bump]
  );

  // target W×H (only editable when surface == 6 "Custom"; else readonly).
  const onTargetDim = useCallback(
    (name: 'target_width' | 'target_height', raw: string | null) => {
      state.current = { ...state.current, [name]: raw == null ? null : raw };
      markChanged();
      bump();
    },
    [markChanged, bump]
  );

  // per-row actual-qty edits (staged → flushed on Select). Keyed by
  // <kind>:<catalogId>. (Legacy setActualQTY pushes onto per-kind arrays.)
  const actualQtyEdits = useRef<
    Record<string, { kind: 'power_supply' | 'monitor' | 'lamp'; catalogId: number; qty: string }>
  >({});
  const onActualQty = useCallback(
    (kind: 'power_supply' | 'monitor' | 'lamp', catalogId: number, qty: string) => {
      actualQtyEdits.current[`${kind}:${catalogId}`] = { kind, catalogId, qty };
      // legacy: changeActualQTY removes #select.non-clickable.
      setSelectEnabled(true);
      markChanged();
    },
    [markChanged, setSelectEnabled]
  );

  // ---- RATE (code-only, userArmed-gated) ----
  // self.model.save({…~25 fields, rate:true}) → server computes → render outputs.
  const buildRatePayload = useCallback((): IUvcRow => {
    const s = state.current;
    const tid = s.uvc_target_target_surface_id;
    const payload: IUvcRow = {
      uvc_target_target_surface_id: tid,
      product_mfg_id: s.mfg_id,
      product_series_id: s.product_series_id,
      voltage_id: s.power_supply_voltage_id,
      // legacy js:1274 — `current_model.target_surface_id || rate_target_id.id`,
      // i.e. the uvc_target_surface JOIN row's pk, NOT the surface pk the
      // <select> is keyed on. The port sent the surface pk, so an Eliminator
      // section rated against join row 3 ("Filter × Coil") instead of 9.
      target_surface_id:
        (detail as IUvcRow | undefined)?.target_surface_id ?? joinRowIdForSurface(tid) ?? tid,
      power_supply_series_id: s.power_supply_series_id,
      master_frameing_kit_id: s.frameing_kit_id ?? null,
      lamp_set_qty: s.lamp_set_qty,
      master_lamp_holder_id: s.lamp_holder_id,
      lamp_to_surface_distance: s.lamp_to_surface_distance,
      uv_repeat_kit_series_id: s.uv_repeat_kit_series_id,
      master_current_sensor_id: s.current_sensor_id,
      master_radio_meter_id: s.radio_meter_id ?? null,
      master_hour_meter_id: s.hour_meter_id ?? null,
      master_toggle_switch_id: s.toggle_switch_id ?? null,
      master_intlk_contact_controller_id: s.intlk_contact_controller_id ?? null,
      master_inter_lock_switch_id: s.inter_lock_switch_id ?? null,
      master_warning_sign_id: s.warning_sign_id ?? null,
      lamp_series_id: s.lamp_series_id,
      lamp_output_id: s.lamp_output_id,
      lamp_finish_id: s.lamp_finish_id,
      master_balast_connector_kit_id: s.balast_connector_kit_id,
      rate: true,
    };
    // ★ target_width / target_height are OMITTED when unset. Legacy reads them
    // off `self.state`, which is only ever written by the surface-6 ("Other")
    // branch (js:678) — for every other surface `state.get('target_width')` is
    // `undefined` and Backbone's `save({patch:true})` → JSON.stringify DROPS the
    // key, so the PATCH never carries it. The port sent the React state's
    // `null` instead, and the serializer rejects it:
    //   400 {"target_width":["This field may not be null."],"target_height":[…]}
    // — which is why #rate appeared to do nothing (the error was console-only).
    if (s.target_width != null && s.target_width !== '') payload.target_width = s.target_width;
    if (s.target_height != null && s.target_height !== '') payload.target_height = s.target_height;
    return payload;
  }, [detail, joinRowIdForSurface]);

  const doRate = useCallback(async () => {
    if (!requiredFilled) return; // #rate gated on required fields
    userArmed.current = true; // a real user click arms the write
    setFooterVisible(false); // legacy js:1227, before the PATCH goes out
    const startedAt = Date.now();
    const trackUvc = (success: boolean) =>
      track('component_rated', {
        component: 'uvc',
        manufacturer: 'database',
        success,
        results: success ? 1 : 0,
        duration_ms: Date.now() - startedAt,
        source: 'board',
      });
    try {
      const result = await rateUvc({ id: modelId, data: buildRatePayload() }).unwrap();
      trackUvc(true);
      setRated(result);
      // legacy render_outputs re-renders the sheet from its template, so the
      // Components tab is active again and the toggle is back to scheduled-only
      // (js:1310 + the `in active` on the Components pane).
      setPartsMode('scheduled');
      setOutputTab('Components');
      // legacy .always → `.footer-left` display:block (js:1298). A rate is the
      // only path that reveals the footer without a tab click.
      setFooterVisible(true);
      ratedDatetime.current = (result.rating_datetime as string) ?? null;
      // legacy .always: #rate non-clickable, #select clickable.
      setSelectEnabled(true);
      bump();
    } catch (err) {
      trackUvc(false);
      // eslint-disable-next-line no-console
      console.error('[uvc selector] rate failed', err);
    }
  }, [requiredFilled, rateUvc, modelId, buildRatePayload, setSelectEnabled, bump]);

  // ---- SELECT (code-only, userArmed-gated) — selectUVC, DEDUPED ([NEG]#81) ----
  const getFormShim = (): ComponentFormShim | undefined =>
    window.__FORM_SHIM__?.[modelId];

  const doSelect = useCallback(async () => {
    userArmed.current = true;
    const s = state.current;
    const m = (rated ?? detail ?? {}) as IUvcRow;

    // generic_mfg from the selected mfg option's data-generic (legacy reads the
    // DOM option dataset; mfg 42 'UV Resources' has no generic mfg → false).
    const genericMfg = false;

    // models_field — the ~30 selected fields (legacy selectUVC, DEDUPED: the
    // legacy payload lists lamp_finish_id / master_balast_connector_kit_id twice
    // [NEG]#81 last-wins; here listed once).
    const modelsField: IUvcRow = {
      uvc_target_target_surface_id: s.uvc_target_target_surface_id,
      balast_connector_kit_id: s.balast_connector_kit_id,
      power_supply_series_id: s.power_supply_series_id,
      lamp_series_id: s.lamp_series_id,
      lamp_output_id: s.lamp_output_id,
      lamp_finish_id: s.lamp_finish_id,
      product_mfg_id: s.mfg_id,
      product_series_id: s.product_series_id,
      // join-row pk, as in the rate payload (legacy js:1540).
      target_surface_id:
        (m as IUvcRow).target_surface_id ??
        joinRowIdForSurface(s.uvc_target_target_surface_id) ??
        s.uvc_target_target_surface_id,
      voltage_id: s.power_supply_voltage_id,
      master_frameing_kit_id: s.frameing_kit_id ?? null,
      lamp_set_qty: s.lamp_set_qty,
      master_lamp_holder_id: s.lamp_holder_id,
      lamp_to_surface_distance: s.lamp_to_surface_distance,
      uv_repeat_kit_series_id: s.uv_repeat_kit_series_id,
      master_current_sensor_id: s.current_sensor_id,
      master_radio_meter_id: s.radio_meter_id ?? null,
      master_hour_meter_id: s.hour_meter_id ?? null,
      master_toggle_switch_id: s.toggle_switch_id ?? null,
      master_intlk_contact_controller_id: s.intlk_contact_controller_id,
      master_inter_lock_switch_id: s.inter_lock_switch_id ?? null,
      master_warning_sign_id: s.warning_sign_id ?? null,
      master_balast_connector_kit_id: s.balast_connector_kit_id,
      // per-component actual-qty fields fall back to the scheduled value when
      // the user did not edit them (legacy *_actual_qty || *_scheduled chains).
      frameing_kit_actual_qty: m.framing_instl_kit_actual_qty ?? m.frameing_kit_scheduled,
      lamp_holder_actual_qty: m.lamp_holder_actual_qty ?? m.lamp_holder_kit_scheduled,
      balast_connector_kit_actual_qty:
        m.balast_connector_kit_actual_qty ?? m.balast_connector_kit_scheduled,
      current_sensor_actual_qty: m.current_sensor_actual_qty ?? m.current_sensor_qty,
      radiometer_actual_qty: m.radiometer_actual_qty ?? m.radio_meter_scheduled,
      hour_meter_actual_qty: m.hour_meter_actual_qty ?? m.hour_meter_scheduled,
      toggle_switch_actual_qty: m.toggle_switch_actual_qty ?? m.toggle_switch_scheduled,
      intlk_contact_controller_actual_qty:
        m.intlk_contact_controller_actual_qty ?? m.intlk_contact_controller_scheduled,
      intlk_switch_actual_qty: m.intlk_switch_actual_qty ?? m.inter_lock_switch_scheduled,
      warning_sign_actual_qty: m.warning_sign_actual_qty ?? m.warning_sign_scheduled,
      generic_mfg: genericMfg,
    };
    // same omission as the rate payload — legacy's undefined keys never reach
    // the wire, and the serializer rejects an explicit null.
    if (s.target_width != null && s.target_width !== '') modelsField.target_width = s.target_width;
    if (s.target_height != null && s.target_height !== '')
      modelsField.target_height = s.target_height;

    // Legacy's `.model_field` DOM sweep runs right here, between the literal
    // and the PATCH (uvc.selector.js:1651-1656). It is the ONLY thing that
    // ever sends `powered_by_id` — the control is rendered and editable
    // (`<Select name="powered_by_id" fieldClass="model_field" />`) and
    // `ksdm_ahu_accessory_uvc.powered_by_id` is a writable serializer field,
    // but the key is absent from the literal, so the port discarded every
    // edit to it on Select. The other four model_field controls here are
    // already in the literal above, so the merge is additive (see
    // modelFieldSweep.ts) and changes nothing else.
    mergeSweptFields(
      modelsField as unknown as Record<string, unknown>,
      sweepModelFieldInputs(rootRef.current)
    );

    // params_field — the catalogue id blob (legacy selectUVC params_field;
    // NB the legacy radio/hour swap bug is preserved verbatim for parity:
    // radio_meter_id ← intlk_contact_controller_id, hour_meter_id ← radio_meter_id).
    const paramsField: Record<string, unknown> = {
      power_supply_series_id: s.power_supply_series_id,
      lamp_series_id: s.lamp_series_id,
      lamp_output_id: s.lamp_output_id,
      lamp_finish_id: s.lamp_finish_id,
      balast_connector_kit_id: s.balast_connector_kit_id,
      radio_meter_id: s.intlk_contact_controller_id,
      hour_meter_id: s.radio_meter_id,
      intlk_contact_controller_id: s.intlk_contact_controller_id,
      inter_lock_switch_id: s.inter_lock_switch_id,
      warning_sign_id: s.warning_sign_id,
      uvc_target_target_surface_id: s.uvc_target_target_surface_id,
      lamp_holder_id: s.lamp_holder_id,
      frameing_kit_id: s.frameing_kit_id,
      toggle_switch_id: s.toggle_switch_id,
    };
    if (s.target_width != null && s.target_width !== '') paramsField.target_width = s.target_width;
    if (s.target_height != null && s.target_height !== '')
      paramsField.target_height = s.target_height;

    // The PATCH chain is registered with the pending-SELECT registry BEFORE it
    // is awaited, so a dialog reopened while it is still in flight waits for it
    // rather than seeding from the pre-Select row (selectWriteGate.ts). `rows`
    // and the whole rated sheet only exist on the server once the models_field
    // PATCH lands — RATE writes nothing (`AHUAccessoryUVC.save()` returns early
    // on `self.rate`).
    const selectChain = (async () => {
      // 1) models_field PATCH (legacy current_model.save(models_field,{patch}))
      await patchModels({ id: modelId, data: modelsField }).unwrap();

      // 2) per-row actual-qty PATCHes (legacy qty loops). Only the rows the user
      //    edited; the legacy "fall back to *_qtys_model when unchanged" loop
      //    re-PATCHes the existing qtys — a no-op write we SKIP (data-safety:
      //    only changed rows write). Documented as a DEVIATION.
      const edits = Object.values(actualQtyEdits.current);
      await Promise.all(
        edits.map((e) =>
          patchActualQty({
            kind: e.kind,
            uvcId: modelId,
            catalogId: e.catalogId,
            qty: e.qty,
          }).unwrap()
        )
      );

      // 3) params_field PATCH (legacy current_model.save({params_field},{patch}))
      await patchParams({ id: modelId, params: paramsField }).unwrap();

      // re-render the owning form + the §30.4 hide cascade.
      const shim = getFormShim();
      shim?.model.set({ ...modelsField, params_field: paramsField });
      shim?.render();
    })();
    trackSelectWrites(selectWriteKey('uvcs', modelId), selectChain);
    try {
      await selectChain;
      track('component_selected', { component: 'uvc' });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[uvc selector] select failed', err);
    }

    // hide cascade (legacy: minRequiredLengthChange after 1 s, then hide +
    // hide:<id> {selected}).
    window.setTimeout(() => ahuBoardBus.emit('minRequiredLengthChange'), 1000);
    ahuBoardBus.emit('rating_mode:hide');
    ahuBoardBus.emit(`rating_mode:hide:${modelId}`, { selected: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rated, detail, modelId, patchModels, patchParams, patchActualQty]);

  // ---- chassis fan-out subscriptions (§30.4) ----
  useEffect(() => {
    const offs = [
      ahuBoardBus.on(
        `uvc_${modelId}:selector:rate` as `${string}_${number}:selector:rate`,
        () => void doRate()
      ),
      ahuBoardBus.on(
        `uvc_${modelId}:selector:select` as `${string}_${number}:selector:select`,
        () => void doSelect()
      ),
      ahuBoardBus.on(
        `uvc_${modelId}:selector:cancel` as `${string}_${number}:selector:cancel`,
        () => {
          // cancelChanges: re-fetch is handled by the chassis hide → RTKQ cache;
          // the React selector just tears down (chassis fires the hide). No write.
        }
      ),
      ahuBoardBus.on(
        `uvc_${modelId}:selector:print` as `${string}_${number}:selector:print`,
        () => {
          // print tmpl (1,068) — STUBBED (only meaningful post-rate; deferred).
          window.print();
        }
      ),
      ahuBoardBus.on('cancel:dialog:changes', () => {
        /* nothing pending to abort (no debounce; rate/select are click-driven). */
      }),
    ];
    return () => offs.forEach((off) => off());
  }, [modelId, doRate, doSelect]);

  // ---- render ----
  if (detailLoading || catalogsLoading || cmsLoading || seeded.current !== modelId) {
    return <div className="preloader dotted-preloader active" />;
  }

  const s = state.current;
  const surfaceId = Number(s.uvc_target_target_surface_id);
  const isCustomTarget = surfaceId === 6;

  // option helpers ---------------------------------------------------------
  const list = (key: UvcCatalogKey): OptionRow[] =>
    (cats[key as keyof typeof cats]?.data as OptionRow[] | undefined) ?? [];

  /** The cascade's narrowed id list for a field, or null before its first load
   * (in which case the select falls back to the full catalog — a superset, so
   * the saved value always renders). */
  const narrowed = (name: string): Array<number | string> | null =>
    graph.optionIds(name);

  // Target Surface options — the hoisted `targetSurfaceRows` memo (see above).
  const surfaceRows = targetSurfaceRows;

  /**
   * The `all_<part>` lists behind the "All Available Parts" view — legacy
   * AllParts() (js:966-1101) narrowing each catalog by the cascade's possible-id
   * list, then render_outputs (js:1330-1449) dropping the model the rate picked.
   *
   * ★ power_supply is the one exception: render_outputs seeds it from the WHOLE
   * catalog (`self.power_supply_coll.toJSON()`, js:1340) and ignores the
   * possible-id narrowing entirely, removing only the scheduled models. That is
   * why dev lists all eight X-Box supplies under a Xbox-PS selection, not just
   * the ones its series allows — checked against dev's own all-parts view.
   */
  const ratedModel = (rated ?? {}) as IUvcRow;
  const scheduledPsModels = new Set(
    ((ratedModel.power_supply_qtys_model as Array<{ model?: string }> | undefined) ?? []).map(
      (r) => r.model
    )
  );
  const allParts: UvcAllParts = {
    power_supply: (list('power_supply') as AllPartRow[]).filter(
      (r) => !scheduledPsModels.has(r.model)
    ),
    frameing_kit: excludeSelectedModel(
      narrowCatalog(list('frameing_kit'), narrowed('frameing_kit_id')),
      ratedModel.frameing_kit_model
    ),
    lamp_holder: excludeSelectedModel(
      narrowCatalog(list('lamp_holder'), narrowed('lamp_holder_id')),
      ratedModel.lamp_holder_model
    ),
    balast_conn: excludeSelectedModel(
      narrowCatalog(list('balast_connector_kit'), narrowed('balast_connector_kit_id')),
      ratedModel.balast_connector_kit_model
    ),
    current_sensor: excludeSelectedModel(
      narrowCatalog(list('current_sensor'), narrowed('current_sensor_id')),
      ratedModel.current_sensor_model
    ),
    radio_meter: excludeSelectedModel(
      narrowCatalog(list('radio_meter'), narrowed('radio_meter_id')),
      ratedModel.radiometer_model
    ),
    hour_meter: excludeSelectedModel(
      narrowCatalog(list('hour_meter'), narrowed('hour_meter_id')),
      ratedModel.hour_meter_model
    ),
    contact_controller: excludeSelectedModel(
      narrowCatalog(
        list('intlk_contact_controller'),
        narrowed('intlk_contact_controller_id')
      ),
      ratedModel.intlk_contact_controller_model
    ),
    warning_sign: excludeSelectedModel(
      narrowCatalog(list('warning_sign'), narrowed('warning_sign_id')),
      ratedModel.warning_sign_model
    ),
  };

  const Select = ({
    name,
    catalog,
    valueKey = 'id',
    labelKey,
    none,
    fieldClass = 'search_field',
  }: {
    name: string;
    catalog: UvcCatalogKey;
    valueKey?: string;
    labelKey?: string;
    none?: boolean;
    fieldClass?: string;
  }) => {
    const ids = narrowed(name);
    const all = list(catalog);
    const rows = ids
      ? all.filter((r) => ids.some((i) => String(i) === String(r[valueKey])))
      : all;
    const value = s[name];
    return (
      <select
        name={name}
        data-name={name}
        className={fieldClass}
        value={value == null || value === '' ? '' : String(value)}
        onChange={(e) => onField(name, e.target.value)}
      >
        {none && <option value="" data-value="None">--</option>}
        {!none && (value == null || value === '') && <option value="" />}
        {rows.map((r) => (
          <option key={String(r[valueKey])} value={String(r[valueKey])}>
            {optionLabel(r, labelKey)}
          </option>
        ))}
      </select>
    );
  };

  /**
   * W1.5 (W1-2/W1-3): a CMS-doc-resolved <select>. The option list + the
   * selected-row label come from the UVC CMS doc via uvcCms.ts (legacy
   * mergeIdsWithCMS render + get_from_cms_data), NOT the flat master catalog.
   * The id-array comes from the dependency cascade (legacy parameterPossibleIDs
   * × mergeIdsWithCMS); until a field's first load resolves it is null and the
   * FULL CMS list renders — a superset, so the saved value always shows.
   * value = row.id; <option> text = the resolver's cmsOptionLabel; data-value
   * mirrors legacy (mfg also stamps data-generic). When the saved id isn't in
   * the list (defensive), a synthetic option carries its resolved label so the
   * select never renders blank.
   */
  const CmsSelect = ({
    name,
    none,
    fieldClass = 'search_field',
  }: {
    name: string;
    none?: boolean;
    fieldClass?: string;
  }) => {
    const value = s[name];
    const rows = cmsOptionList(cmsDoc as UvcCmsDoc | undefined, name, narrowed(name));
    const valStr = value == null || value === '' ? '' : String(value);
    const hasValueRow = rows.some((r) => String(r.id) === valStr);
    const selectedLabel =
      valStr !== '' ? cmsSelectedLabel(cmsDoc as UvcCmsDoc | undefined, name, value as number) : '';
    return (
      <select
        name={name}
        data-name={name}
        className={fieldClass}
        value={valStr}
        onChange={(e) => onField(name, e.target.value)}
      >
        {none && (
          <option value="" data-value="None">
            --
          </option>
        )}
        {!none && valStr === '' && <option value="" />}
        {rows.map((r) => (
          <option
            key={String(r.id)}
            value={String(r.id)}
            data-value={String(r.label ?? '')}
            {...(name === 'mfg_id'
              ? { 'data-generic': String((r.generic_mfg as string | undefined) ?? 'none') }
              : {})}
          >
            {String(r.label ?? '')}
          </option>
        ))}
        {/* defensive: saved id not in the (full) CMS list → keep it visible */}
        {!none && valStr !== '' && !hasValueRow && (
          <option value={valStr}>{selectedLabel || valStr}</option>
        )}
      </select>
    );
  };

  // uvc.selector.tmpl:22 — `tab-pane fade scrollable-content col-xs-12 row
  // float-left`. The port dropped `col-xs-12 row`: together they are a
  // self-cancelling gutter (padding 0 15px + margin 0 -15px on a width:100%
  // border-box), so the pane keeps the tab-content's full width but its
  // CONTENT box is 30px narrower — which is the field-column width dev shows.
  const tabClass = (t: TabName) =>
    `tab-pane fade scrollable-content col-xs-12 row float-left${
      activeTab === t ? ' in active' : ''
    }`;

  // ★ The legacy Backbone view is `className: "col-xs-12"` (uvc.selector.js:127)
  // — `$('#rating_mode_uvc_content').html(view.$el)` inserts THAT div, and the
  // template's `#coil_new` is its child. The port rendered `#coil_new` straight
  // into the host, losing the wrapper's 15px left/right gutter: the whole input
  // area sat 15px further left and ~30px wider than dev (measured on dev3:
  // `#coil_new` left 586 against dev's 601). Note this is per-selector — fan
  // (fan.selector.js:601) has the same className, gas heater's is commented
  // out, filter uses its own, coil/EW/PHX have none.
  return (
    <div ref={rootRef} className="col-xs-12">
    <div id="coil_new" className="coil_new coil-rating-mode uvc-input rating">
      {/* ===== Input tab nav ===== */}
      <div className="col-xs-1 nav nav-tabs">
        <ul>
          <li className="cursor-default">Input</li>
          {(['Project', 'Properties', 'Lamp', 'Options'] as TabName[]).map((t) => (
            <li key={t} className={activeTab === t ? 'active' : undefined}>
              <a
                className={activeTab === t ? 'active' : undefined}
                onClick={() => setActiveTab(t)}
              >
                {t}
              </a>
            </li>
          ))}
        </ul>
      </div>

      <div className="col-xs-11 tab-content full-height right">
        {/* ----- Project ----- */}
        <div className={tabClass('Project')} id="ahu_Project">
          <div className="col-xs-12 col-md-12 col-lg-6">
            <LabeledReadOnly label="Project Name" value={projectName} />
            <LabeledReadOnly label="AHU" value={ahuTag} />
            <LabeledReadOnly label="Section" value={sectionLabel} />
          </div>
        </div>

        {/* ----- Properties ----- */}
        <div className={tabClass('Properties')} id="ahu_Properties">
          <div className="col-xs-12 col-md-12 col-lg-6">
            <Row label="Manufacturer" dataName="mfg_id">
              {/* W1.5 (folds W1-1): mfg resolved from the UVC CMS `mfg` path
                  (single row id 42 "UV Resources") via CmsSelect — same resolver
                  as every other dependent select. Previously a hand-rolled static
                  option reading detail.uvc_mfg; now the CMS doc is the source. */}
              <CmsSelect name="mfg_id" fieldClass="lg-input" />
            </Row>
            <Row label="Product" dataName="product_series_id">
              {/* W1-2 FIX: was bound to the `lamp` MASTER catalog (showed
                  "SEL-33-HO-T5"); the product labels live in the CMS doc `series`
                  path ("RLM Extreme" = 749). */}
              <CmsSelect name="product_series_id" fieldClass="has_default search_field" />
            </Row>
            <Row label="Power Supply" dataName="power_supply_series_id">
              {/* W1-3 FIX: CMS `power_supply.series` (label = series). */}
              <CmsSelect name="power_supply_series_id" fieldClass="search_field model_field" />
            </Row>
            <Row label="Voltage" dataName="power_supply_voltage_id">
              {/* W1-3 FIX: CMS `power_supply.voltage` (label = name, e.g. 120/1/60). */}
              <CmsSelect name="power_supply_voltage_id" fieldClass="search_field" />
            </Row>
            <Row label="Powered By" dataName="powered_by_id">
              <Select name="powered_by_id" catalog="powered_by" fieldClass="model_field" />
            </Row>
          </div>
          <div className="col-xs-12 col-md-12 col-lg-6">
            <Row label="Target Surface" dataName="uvc_target_target_surface_id" nested rightRow>
              <select
                name="uvc_target_target_surface_id"
                data-name="uvc_target_target_surface_id"
                className="uvc_target_target_surface has_default"
                value={surfaceId ? String(surfaceId) : ''}
                onChange={(e) => onField('uvc_target_target_surface_id', e.target.value)}
              >
                {surfaceRows.map((r) => (
                  <option key={String((r.surface as OptionRow)?.id ?? r.id)} value={String((r.surface as OptionRow)?.id ?? r.id)}>
                    {optionLabel((r.surface as OptionRow) ?? r, 'name')}
                  </option>
                ))}
              </select>
            </Row>
            {/* target W×H — editable only for surface 6 (Custom) */}
            <div className="row TargetWHInputs">
              <div className="col-xs-4 label-part">
                <div className="right-row justify-content-between">
                  <span>
                    <span data-name="target_width">Target W </span> x{' '}
                    <span className="required" data-name="target_height"> H </span>
                  </span>
                  <span className="percent right">in</span>
                </div>
              </div>
              <div className="col-xs-8">
                <div className="row">
                  <div className="col-xs-6 input-part" style={{ paddingRight: 0 }}>
                    <input
                      name="target_width"
                      data-name="target_width"
                      className={`uvc_target uvc-width${isCustomTarget ? '' : ' non-clickable'}`}
                      style={{ width: 'calc(100% - 15px)' }}
                      data-formatter_type="decimal"
                      readOnly={!isCustomTarget}
                      value={String(
                        s.target_width ??
                          (isCustomTarget ? ((detail as IUvcRow)?.section_aw_width ?? '') : '')
                      )}
                      onChange={(e) => onTargetDim('target_width', e.target.value)}
                    />
                    <span className="percent center">x</span>
                  </div>
                  <div className="col-xs-6 input-part">
                    <input
                      name="target_height"
                      data-name="target_height"
                      className={`uvc_target${isCustomTarget ? '' : ' non-clickable'}`}
                      data-formatter_type="decimal"
                      readOnly={!isCustomTarget}
                      value={String(
                        s.target_height ??
                          (isCustomTarget ? ((detail as IUvcRow)?.section_aw_height ?? '') : '')
                      )}
                      onChange={(e) => onTargetDim('target_height', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
            <SpacerRow />
            <Row label="Framing Instl Kit" dataName="frameing_kit_id" nested>
              {/* W1-3 class: CMS `framing_kit.model` (label = model, e.g. INSTL-KIT). */}
              <CmsSelect name="frameing_kit_id" none />
            </Row>
          </div>
        </div>

        {/* ----- Lamp ----- */}
        <div className={tabClass('Lamp')} id="ahu_Lamp">
          <div className="col-xs-12 col-md-12 col-lg-6">
            <Row label="Series" dataName="lamp_series_id" rightRow>
              {/* W1-3 FIX: CMS `lamp.series` (label = series, e.g. UVR-RLM-HO). */}
              <CmsSelect name="lamp_series_id" fieldClass="lg-input required_field search_field has_default" />
            </Row>
            <Row label="Set" dataName="lamp_set_qty" rightRow>
              <input
                type="text"
                data-min="1"
                data-formatter_type="integer"
                name="lamp_set_qty"
                className="model_field"
                value={String(s.lamp_set_qty ?? '')}
                onChange={(e) => onNumberField('lamp_set_qty', e.target.value)}
              />
            </Row>
            <Row label="Output" dataName="lamp_output_id" rightRow>
              {/* W1-3 class: CMS `lamp.lamp_output` (label = name, e.g. High). */}
              <CmsSelect name="lamp_output_id" />
            </Row>
            <Row label="Finish" dataName="lamp_finish_id" nested rightRow>
              {/* W1-3 class: CMS `lamp.finish` (label = name, e.g. Standard). */}
              <CmsSelect name="lamp_finish_id" fieldClass="search_field model_field" />
            </Row>
            <Row label="Holder" dataName="lamp_holder_id" rightRow>
              {/* W1-3 class: CMS `lamp_holder.model` (label = model, e.g. RLM-DLH-EB). */}
              <CmsSelect name="lamp_holder_id" />
            </Row>
          </div>
          <div className="col-xs-12 col-md-12 col-lg-6">
            <Row label="Lamp to Surface" dataName="lamp_to_surface_distance" unit="in" required rightRow>
              <input
                type="text"
                data-min="6.00"
                data-max="24"
                data-formatter_type="decimal"
                name="lamp_to_surface_distance"
                className="model_field"
                value={String(s.lamp_to_surface_distance ?? '12.00')}
                onChange={(e) => onNumberField('lamp_to_surface_distance', e.target.value)}
              />
            </Row>
            <Row label="Balast Connector Kit" dataName="balast_connector_kit_id" nested rightRow>
              {/* W1-3 class: CMS `balast_connector_kit.model` (label = model). */}
              <CmsSelect name="balast_connector_kit_id" />
            </Row>
            <Row label="Remote Monitor" dataName="uv_repeat_kit_series_id" nested rightRow>
              {/* W1-3 class: CMS `uv_repeat_kit.series` (label = series). */}
              <CmsSelect name="uv_repeat_kit_series_id" />
            </Row>
            <Row label="Current Sensor" dataName="current_sensor_id" rightRow>
              {/* W1-3 class: CMS `current_sensor.model` (label = model, e.g. CU2). */}
              <CmsSelect name="current_sensor_id" />
            </Row>
          </div>
        </div>

        {/* ----- Options ----- */}
        <div className={tabClass('Options')} id="ahu_Options">
          <div className="col-xs-12 col-md-12 col-lg-6">
            <Row label="Radiometer" dataName="radio_meter_id" nested>
              {/* W1-3 class: CMS `radio_meter` (label = model). */}
              <CmsSelect name="radio_meter_id" none />
            </Row>
            <Row label="Hour Meter" dataName="hour_meter_id" nested>
              {/* W1-3 class: CMS `hour_meter` (label = model). */}
              <CmsSelect name="hour_meter_id" none />
            </Row>
            <Row label="Toggle Switch" dataName="toggle_switch_id" nested>
              {/* toggle_switch is a CUSTOM_COLLECTION (master catalog), not a CMS
                  path — keep the master-list Select (legacy mergeIdsWithCMS
                  CUSTOM_COLLECTIONS branch). */}
              <Select name="toggle_switch_id" catalog="toggle_switch" none />
            </Row>
          </div>
          <div className="col-xs-12 col-md-12 col-lg-6" id="Connections">
            <Row label="Interlock Controller" dataName="intlk_contact_controller_id" nested>
              {/* W1-3 class: CMS `intlk_contact_controller.model` (label = model). */}
              <CmsSelect name="intlk_contact_controller_id" none />
            </Row>
            <Row label="Interlock Switch" dataName="inter_lock_switch_id" nested>
              {/* W1-3 class: CMS `inter_lock_switch.model` (label = model). */}
              <CmsSelect name="inter_lock_switch_id" none />
            </Row>
            <Row label="Warning Sign" dataName="warning_sign_id" nested>
              {/* W1-3 class: CMS `warning_sign.model` (label = model, e.g. WS-12X8). */}
              <CmsSelect name="warning_sign_id" none />
            </Row>
          </div>
        </div>
      </div>
      </div>

      {/* ===== OUTPUT =====
          Legacy renders `.output-part` EMPTY (uvc.selector.tmpl:424) and only
          ever fills it from render_outputs after a rate (js:1452); every field
          change empties it again (js:674/:902/:927). The port rendered the
          Components/Electrical/Notes strip + an empty sheet unconditionally, so
          an unrated UVC opened showing an output panel dev does not have. The
          container stays (the `.uvc-output` scheduled/all-parts classes hang off
          it); only its contents are gated. */}
      <div
        className={`coil_new output-part uvc-output rated-item output-group rating ${
          partsMode === 'all' ? 'show_all_parts' : 'show_scheduled_parts'
        }`}
      >
        {rated && (
          <UvcRatedOutputs
            rated={rated}
            allParts={allParts}
            onActualQty={onActualQty}
            partsMode={partsMode}
            tab={outputTab}
            onTab={onOutputTab}
          />
        )}
      </div>

      {/* siblings of `.output-part`, as in uvc.selector.tmpl:425 / :428 */}
      {rated && !rated.error && footerVisible && (
        <UvcOutputFooter partsMode={partsMode} onPartsMode={setPartsMode} />
      )}
      {rated && ratedDatetime.current && (
        <div className="col-xs-11 col-xs-push-1">
          <div className="row rating-time">Rated on : {ratedDatetime.current}</div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// small presentational helpers
// ---------------------------------------------------------------------------
/**
 * One label + control row. The legacy template writes these in TWO shapes and
 * the difference is visible, which is what the spacing report is about:
 *
 *   flat    <div class="col-xs-8 input-part">…</div>
 *   nested  <div class="col-xs-8"><div class="row">
 *             <div class="col-xs-12 input-part">…</div></div></div>
 *
 * `#rating_mode_uvc_content .uvc-input.rating .tab-pane .row` carries
 * `padding: 2px 0` (common.css:17403-17406) and matches the INNER row too, so a
 * nested row sits ~4px taller. The port flattened every row, which closed the
 * gaps dev shows around Framing Instl Kit, Finish, Balast Connector Kit and
 * Remote Monitor, and across the whole Options tab.
 *
 * `rightRow` reproduces the legacy `div.right-row` label wrapper
 * (`max-height:29px; display:flex; align-items:center`, common.css:15907).
 */
const Row = ({
  label,
  dataName,
  unit,
  required,
  nested,
  rightRow,
  children,
}: {
  label: string;
  dataName: string;
  unit?: string;
  required?: boolean;
  /** legacy Shape B — the extra col-xs-8 > .row > .col-xs-12.input-part wrap. */
  nested?: boolean;
  /** legacy wraps this label in `div.right-row`. */
  rightRow?: boolean;
  children: React.ReactNode;
}) => {
  const labelSpan = (
    <>
      <span className={required ? 'required' : undefined} data-name={dataName}>
        {label}
      </span>
      {unit && <span className="percent right">{unit}</span>}
    </>
  );
  return (
    <div className="row">
      <div className="col-xs-4 label-part">
        {rightRow ? (
          <div className={`right-row${unit ? ' justify-content-between' : ''}`}>{labelSpan}</div>
        ) : (
          labelSpan
        )}
      </div>
      {nested ? (
        <div className="col-xs-8">
          <div className="row">
            <div className="col-xs-12 input-part">{children}</div>
          </div>
        </div>
      ) : (
        <div className="col-xs-8 input-part">{children}</div>
      )}
    </div>
  );
};

/**
 * The blank row the legacy Properties template sits between the Target W x H
 * block and Framing Instl Kit (uvc.selector.tmpl:164-171). It is not decorative
 * — `.label-part`'s 7px top/bottom padding gives it ~19px of height, which is
 * the gap dev shows there and the port did not.
 */
const SpacerRow = () => (
  <div className="row">
    <div className="col-xs-4 label-part">
      <div className="right-row" />
    </div>
    <div className="col-xs-8 input-part" />
  </div>
);

const LabeledReadOnly = ({ label, value }: { label: string; value?: unknown }) => (
  <div className="col-xs-12 row">
    <div className="col-xs-6 label-part">
      <div className="row">{label}</div>
    </div>
    <div className="col-xs-6 input-part">
      <input
        className="lg-input"
        type="text"
        value={value == null ? '' : String(value)}
        readOnly
        tabIndex={-1}
        onChange={() => undefined}
      />
    </div>
  </div>
);

export default UvcSelector;
